import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { MotifTable } from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleFirstDataRendered,
  handleItemsPerPageChange,
  handleGridReady,
} from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import MasterDataManagementPropertiesCellRenderer from '../shared/customRenderers/MasterDataManagementPropertiesCellRenderer';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
import * as formatters from '../../scripts/formFormatters';
import DateComparator from '../shared/customComparators/DateComparator';
import DateCellRenderer from '../checklistDeliveryCalendar/customRenderers/DateCellRenderer';

// Helper functions moved outside component to prevent recreation on every render
const FormattedCurrency = ({ value }) => {
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const SanitizeNotSelectedValue = (value) => {
  return value ? value.replace('Not Selected', '') : '';
};

const MasterDataManagementPropertiesTable = ({
  propertiesData,
  handleEditButtonClick,
  handleDeleteButtonClick,
  handleGridChanges,
  gridColumnState,
  refreshGridPropertyData,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 150,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const dateOptions = useMemo(
    () => ({
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    []
  );

  // Memoize column definitions to prevent recreation on every render
  const columnDefs = useMemo(() => {
    return [
      {
        field: 'reitTrackingID',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        minWidth: 220,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDForREIT'),
        },
      },
      {
        field: 'reitShortName',
        headerName: 'REIT Name',
        sortable: true,
        minWidth: 200,
        headerComponentParams: { template: CustomHeaderTemplate('REITName') },
      },
      {
        field: 'isActive',
        headerName: 'REIT Status',
        sortable: true,
        minWidth: 155,
        headerComponentParams: { template: CustomHeaderTemplate('REITStatus') },
        valueGetter: (params) => {
          const statusValue =
            params.data && params.data.isActive ? params.data.isActive : null;
          return statusValue ? 'Active' : 'Inactive';
        },
      },
      {
        field: 'propertyTrackingID',
        headerName: 'REITSuite Entity ID for Property',
        sortable: true,
        minWidth: 235,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDForProperty'),
        },
      },
      {
        field: 'gtpEntityID',
        headerName: 'GTP Entity ID',
        sortable: true,
        minWidth: 165,
        headerComponentParams: {
          template: CustomHeaderTemplate('GTPEntityID'),
        },
      },
      {
        field: 'clientEntityID',
        headerName: 'Client Entity ID',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('ClientEntityID'),
        },
      },
      {
        field: 'propertyName',
        headerName: 'Property Name',
        sortable: true,
        minWidth: 250,
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyName'),
        },
      },
      {
        field: 'street',
        headerName: 'Street Address',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('StreetAddress'),
        },
      },
      {
        field: 'city',
        headerName: 'City',
        sortable: true,
        minWidth: 135,
        headerComponentParams: { template: CustomHeaderTemplate('City') },
      },
      {
        field: 'state',
        headerName: 'State',
        sortable: true,
        minWidth: 145,
        headerComponentParams: { template: CustomHeaderTemplate('State') },
      },
      {
        field: 'zipCode',
        headerName: 'ZIP Code',
        sortable: true,
        minWidth: 150,
        headerComponentParams: { template: CustomHeaderTemplate('ZIPCode') },
      },

      {
        field: 'purchasePrice',
        headerName: 'Purchase Price',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('PurchasePrice'),
        },
        valueGetter: (params) => {
          return FormattedCurrency({ value: params.data.purchasePrice });
        },
      },
      {
        field: 'acquisitionDate',
        headerName: 'Acquisition Date',
        sortable: true,
        minWidth: 185,
        headerComponentParams: {
          template: CustomHeaderTemplate('AcquisitionDate'),
        },
        valueGetter: (params) => {
          const acquisitionDateValue =
            params.data && params.data.acquisitionDate
              ? params.data.acquisitionDate
              : null;
          return acquisitionDateValue
            ? formatters.formatDate(acquisitionDateValue)
            : null;
        },
      },
      {
        field: 'initialQuarter',
        headerName: 'Initial Quarter',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('InitialQuarter'),
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        sortable: true,
        minWidth: 150,
        headerComponentParams: { template: CustomHeaderTemplate('EndDate') },
        valueGetter: (params) => {
          const endDateValue =
            params.data && params.data.endDate ? params.data.endDate : null;
          return endDateValue ? formatters.formatDate(endDateValue) : null;
        },
      },
      {
        field: 'finalQuarter',
        headerName: 'Final Quarter',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('FinalQuarter'),
        },
      },
      {
        field: 'rentableSquareFootage',
        headerName: 'Rentable Sq. Ft.',
        sortable: true,
        minWidth: 170,
        valueGetter: (params) => {
          const rentableValue =
            params.data && params.data.rentableSquareFootage
              ? params.data.rentableSquareFootage
              : null;
          return rentableValue ? formatters.formatNumber(rentableValue) : null;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('RentableSqFt'),
        },
      },
      {
        field: 'propertyType',
        headerName: 'Property Type',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyType'),
        },
        valueGetter: (params) => {
          return params.data && params.data.propertyType
            ? SanitizeNotSelectedValue(params.data.propertyType)
            : null;
        },
      },
      {
        field: 'psqPropertyTypes',
        headerName: 'PSQ Property Type(s)',
        sortable: true,
        minWidth: 200,
        headerComponentParams: {
          template: CustomHeaderTemplate('PSQPropertyType'),
        },
        valueGetter: (params) => {
          const psqPropertyTypesValue =
            params.data && params.data.psqPropertyTypes
              ? params.data.psqPropertyTypes
              : null;
          return psqPropertyTypesValue
            ? psqPropertyTypesValue
                .map((p) => {
                  return p
                    .replace('MultiFamily', 'Multi-Family')
                    .replace('NotSelected', '');
                })
                .join(', ')
            : null;
        },
      },
      {
        field: 'propertyClass',
        headerName: 'Class',
        sortable: true,
        minWidth: 145,
        valueGetter: (params) => {
          return params.data && params.data.propertyClass
            ? SanitizeNotSelectedValue(params.data.propertyClass)
            : null;
        },
        headerComponentParams: { template: CustomHeaderTemplate('Class') },
      },
      {
        field: 'tenantType',
        headerName: 'Tenant Type',
        sortable: true,
        minWidth: 155,
        valueGetter: (params) => {
          return params.data && params.data.tenantType
            ? SanitizeNotSelectedValue(params.data.tenantType)
            : null;
        },
        headerComponentParams: { template: CustomHeaderTemplate('TenantType') },
      },
      {
        field: 'parkingType',
        headerName: 'Parking',
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => {
          return params.data && params.data.parkingType
            ? SanitizeNotSelectedValue(params.data.parkingType)
            : null;
        },
        headerComponentParams: { template: CustomHeaderTemplate('Parking') },
      },
      {
        field: 'managementCompany',
        headerName: 'Management Company',
        sortable: true,
        minWidth: 200,
        headerComponentParams: {
          template: CustomHeaderTemplate('ManagementCompany'),
        },
      },
      {
        field: 'managerName',
        headerName: 'Manager Name',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('ManagerName'),
        },
      },
      {
        field: 'managerEmail',
        headerName: 'Manager Email',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('ManagerEmail'),
        },
      },
      {
        field: 'managerPhone',
        headerName: 'Manager Phone',
        sortable: true,
        minWidth: 170,
        headerComponentParams: {
          template: CustomHeaderTemplate('ManagerPhone'),
        },
        valueGetter: (params) => {
          const phoneValue =
            params.data && params.data.managerPhone
              ? params.data.managerPhone
              : null;
          return phoneValue ? formatters.formatPhone(phoneValue) : null;
        },
      },
      {
        field: 'accountantName',
        headerName: 'Accountant Name',
        sortable: true,
        minWidth: 185,
        headerComponentParams: {
          template: CustomHeaderTemplate('AccountantName'),
        },
      },
      {
        field: 'accountantEmail',
        headerName: 'Accountant Email',
        sortable: true,
        minWidth: 185,
        headerComponentParams: {
          template: CustomHeaderTemplate('AccountantEmail'),
        },
      },
      {
        field: 'accountantPhone',
        headerName: 'Accountant Phone',
        sortable: true,
        minWidth: 185,
        headerComponentParams: {
          template: CustomHeaderTemplate('AccountantPhone'),
        },
        valueGetter: (params) => {
          const phoneValue =
            params.data && params.data.accountantPhone
              ? params.data.accountantPhone
              : null;
          return phoneValue ? formatters.formatPhone(phoneValue) : null;
        },
      },
      {
        field: 'lastUpdatedBy',
        headerName: 'Last Updated By',
        sortable: true,
        minWidth: 185,
        hide: true,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedBy'),
        },
      },
      {
        field: 'lastUpdatedDate',
        headerName: 'Last Updated Date',
        sortable: true,
        minWidth: 200,
        hide: true,
        cellRenderer: DateCellRenderer,
        cellRendererParams: { dateFieldName: 'lastUpdatedDate' },
        filterValueGetter: (params) => {
          const dateValue =
            params.data && params.data.lastUpdatedDate
              ? params.data.lastUpdatedDate
              : null;
          return dateValue
            ? new Date(dateValue + 'Z').toLocaleDateString('en-US', dateOptions)
            : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedDate'),
        },
      },
      {
        field: 'editactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: MasterDataManagementPropertiesCellRenderer,
        cellRendererParams: { handleEditButtonClick },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: MasterDataManagementPropertiesCellRenderer,
        cellRendererParams: { handleDeleteButtonClick },
      },
    ];
  }, [handleEditButtonClick, handleDeleteButtonClick, dateOptions]);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
    if (rowData.length > 0 && gridRef?.current?.api !== undefined) {
      let calcTotalPages =
        gridRef &&
        gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      if (calcTotalPages === 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(propertiesData);
  }, [propertiesData]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    gridRef.current.api.paginationGoToPage(pageNumber - 1);
  };

  const instructionText =
    'Click on Actions > Click Export Property Import Template > Update the downloaded file with the property details > Click Upload Property Import Template.';
  const overlayNoRowsTemplate = `<div class="mb-3">${instructionText}</div>`;
  const hasData = propertiesData && propertiesData.length > 0;

  const onCellKeyDown = (event) => {
    if (event.event.key === 'Enter' || event.event.key === ' ') {
      if (event.colDef.field == 'editactions') {
        handleEditButtonClick(event.data);
      }
      if (event.data.canDeleteProperty) {
        if (event.colDef.field == 'deleteactions') {
          handleDeleteButtonClick(event.data);
        }
      }
    }
  };

  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div
            className={`ag-theme-quartz motif-table ${hasData ? 'table-bd-full-height-width' : 'empty-table-container'}`}
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRendered(gridRef)}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged(
                gridRef,
                currentPage,
                setCurrentPage,
                setTotalPages
              )}
              data-testid="grdPropertiesDataGrid"
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              onCellKeyDown={onCellKeyDown}
            />

            {rowData?.length > 0 && (
              <div className="mt-3">
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

MasterDataManagementPropertiesTable.propTypes = {
  propertiesData: PropTypes.array.isRequired,
  handleEditButtonClick: PropTypes.func,
  handleDeleteButtonClick: PropTypes.func,
  handleViewButtonClick: PropTypes.func,
  handleGridChanges: PropTypes.func.isRequired,
  gridColumnState: PropTypes.object,
  handleGridColumnStateChange: PropTypes.func.isRequired,
  refreshGridPropertyData: PropTypes.object,
};

export default MasterDataManagementPropertiesTable;
